import { takeEvery, takeLatest, all, select, call, put, debounce, spawn, delay, race, take, fork, cancel, join } from 'redux-saga/effects';
import * as actions from './actions.js';
import * as selectors from './selectors.js';
import * as boardSelectors from '../board/selectors.js';
import * as chatActions from '../chat/actions.js';
import * as cardDetailsActions from '../card-details/actions.js';
import * as chatSelectors from '../chat/selectors.js';
import * as cardDetailsSelectors from '../card-details/selectors.js';
import * as boardHighlightsSelectors from '../board-highlights/selectors.js';
import * as router from '../router/actions.js';
import * as routerSelectors from '../router/selectors.js';
import { requestApi, isNetworkError } from '../../request-api.js';
import firestoreRedux from '@dreamworld/firestore-redux';
import { show as showSnackBar } from '../../components/kerika-snackbar.js';
import i18next from '@dw/i18next-esm';
import { retry } from '@lifeomic/attempt';
import { resolveTemplate } from '../../utils.js';
import { buildContextKey, promptToMarkdown, promptToEditorValue, isQuickActionAllowed } from './utils.js';
import { escapeHtml, toPlainText, toEditorValue } from '../references/utils.js';
import { titleOf } from '../references/anchors.js';
import { markdownToHtml } from '../../utils/markdown-to-html.js';
import entityIdProvider from '../../entity-id-provider.js';
import * as auth from '../auth';
import * as feature from '../feature';
import { clipboard } from '../../components/clip-board.js';
import * as knownFeatures from '../known-features';
import * as signup from '../signup';
import * as analytics from '../../analytics/analytics.js';
import isEmpty from 'lodash-es/isEmpty.js';
import get from 'lodash-es/get.js';
import { marked } from 'marked';
import * as attachments from '../attachments/index.js';


/**
 * A request's `context`: the board signals and the page draft, whose editor HTML goes as Markdown, as the prompt does.
 * @returns {Object|undefined}
 */
function* _requestContext(draft, implicitContext) {
  if (!draft) return implicitContext;
  const description = draft.description && (yield call(promptToMarkdown, draft.description));
  const chatMessageText = draft.chatMessage?.text && (yield call(promptToMarkdown, draft.chatMessage.text));
  return {
    ...implicitContext,
    draft: {
      ...draft,
      ...(description && { description }),
      ...(chatMessageText && { chatMessage: { ...draft.chatMessage, text: chatMessageText } }),
    },
  };
}

/**
 * Polls Redux state until all file refs have completed uploading, or auto-submit is cleared.
 * Returns 'SUCCESS', 'FAILED', 'CANCELED' (user stopped), or 'TIMEOUT'.
 */
function* waitForUploads(refs, contextKey) {
  const deadline = Date.now() + 5 * 60 * 1000; // 5 min max
  while (Date.now() < deadline) {
    const state = yield select();
    if (!selectors.autoSubmitPending(state, contextKey)) return 'CANCELED';
    const counts = attachments.aichat.selectors.selectFileStatusCounts(state, refs);
    if (counts.failed > 0 || counts.canceled > 0) return 'FAILED';
    if (counts.uploading === 0) return 'SUCCESS';
    yield delay(300);
  }
  return 'TIMEOUT';
}

// Track previous message status to detect changes from IN_PROGRESS to SUCCESS
let previousMessageStatus = {};

// Track pending board creation tools waiting for message SUCCESS
// Key: chatId, Value: { tool, statusKey }
let pendingBoardCreationTools = {};

// Track previous chat IDs to detect changes
let previousPageChatId = null;
let previousDialogChatId = null;

// Track previous recent chat query ID for cleanup
let previousRecentChatQueryId = null;

// Track previous quick actions query ID for cleanup
let previousQuickActionsQueryId = null;
let defaultQuickActionsLoaded = false;

// Track whether AiChatPreferences have been loaded for the current user

/**
 * Disconnects all Firestore live listeners for a specific chat.
 * This includes chat document, messages, and tool messages queries.
 *
 * @param {String} chatId - The chat ID to disconnect listeners for
 */
function* disconnectFirestore(chatId) {
  if (!chatId) return;
  firestoreRedux.cancelQueryByRequester(`ai-chat-${chatId}`);
  firestoreRedux.cancelQueryByRequester(`ai-messages-${chatId}`);
  firestoreRedux.cancelQueryByRequester(`ai-tool-messages-${chatId}`);
  firestoreRedux.cancelQueryByRequester(`ai-chat-attachments-${chatId}`);
  firestoreRedux.cancelQueryByRequester(`ai-tool-cards-${chatId}`);
}

/**
 * Watches for router changes and loads chat data when needed.
 *
 * Since currentContext, currentChatId, showingChatHistory are now managed through URL,
 * this saga only needs to:
 * 1. Load chat data when a chatId appears in the URL
 * 2. Clean up Firestore listeners when AI chat is closed
 *
 * URL formats supported:
 * - ?aichat=new - Opens page-level AI chat with new chat session
 * - ?aichat=new&show-chat-history=true - Opens page-level AI chat with history panel visible
 * - ?aichat=aic_xxx - Opens page-level AI chat with specific chat
 * - ?aichat=aic_xxx&show-chat-history=true - Opens page-level AI chat with specific chat and history panel visible
 * - ?card-aichat=new - Opens dialog-level AI chat with new chat session
 * - ?card-aichat=new&show-chat-history=true - Opens dialog-level AI chat with history panel visible
 * - ?card-aichat=aic_xxx - Opens dialog-level AI chat with specific chat
 * - ?card-aichat=aic_xxx&show-chat-history=true - Opens dialog-level AI chat with specific chat and history panel visible
 */
function* watchRouterSaga() {
  yield spawn(handlePageAiChatSaga);
  yield spawn(handleDialogAiChatSaga);
  yield spawn(prefetchRecentChatSaga);
  yield spawn(loadQuickActionsSaga);

}

/**
 * A surface's previous chat can be disconnected once neither surface shows it: the listeners are
 * keyed by chatId alone, so the page and the dialog share them.
 */
function _isChatLeft(previousChatId, chatId, otherSurfaceChatId) {
  return !!previousChatId && previousChatId !== chatId && previousChatId !== otherSurfaceChatId;
}

/**
 * Handles page-level AI chat data loading and cleanup on route changes.
 *
 * - When a new chatId appears in URL: loads chat data via Firestore listeners
 * - When page AI chat is closed: cancels Firestore listeners for the previous chat
 */
function* handlePageAiChatSaga() {
  const state = yield select();
  const pageAiChat = routerSelectors.pageAiChat(state);

  if (pageAiChat.chatId && pageAiChat.chatId !== previousPageChatId) {
    yield call(loadChatDataSaga, pageAiChat.chatId);
  }
  if (_isChatLeft(previousPageChatId, pageAiChat.chatId, routerSelectors.dialogAiChatId(state))) {
    yield spawn(disconnectFirestore, previousPageChatId);
  }
  previousPageChatId = pageAiChat.chatId;
}

/**
 * Handles dialog-level AI chat data loading and cleanup on route changes.
 *
 * - When a new chatId appears in dialog URL: loads chat data via Firestore listeners
 * - When dialog AI chat is closed: cancels Firestore listeners for the previous chat
 */
function* handleDialogAiChatSaga() {
  const state = yield select();
  const dialogAiChat = routerSelectors.dialogAiChat(state);

  if (dialogAiChat.chatId && dialogAiChat.chatId !== previousDialogChatId) {
    yield call(loadChatDataSaga, dialogAiChat.chatId);
  }
  if (_isChatLeft(previousDialogChatId, dialogAiChat.chatId, routerSelectors.pageAiChatId(state))) {
    yield spawn(disconnectFirestore, previousDialogChatId);
  }
  previousDialogChatId = dialogAiChat.chatId;
}

/**
 * Prefetches the most recent chat for the current page context.
 *
 * Dispatches a live Firestore query (limit 1, ordered by latestMessageSentAt desc)
 * so data is ready when user clicks the FAB button. Cancels the previous query
 * when context changes.
 *
 * Called by watchRouterSaga on every router change to keep the prefetched data current.
 */
function* prefetchRecentChatSaga() {
  const state = yield select();
  const userId = auth.selectors.currentUserId(state);
  if (!userId) return;

  const context = selectors.pageContext(state);
  if (!context) return;

  const { type: contextType, boardId: contextBoardId, cardId: contextCardId } = context;

  const queryId = selectors.recentChatQueryId(contextType, contextBoardId, contextCardId);
  if (queryId === previousRecentChatQueryId) return;

  // Cancel previous query
  if (previousRecentChatQueryId) {
    firestoreRedux.cancelQueryByRequester(previousRecentChatQueryId);
  }

  // Build where clause based on context
  let whereClause;
  switch (contextType) {
    case 'CARD':
      whereClause = [
        ['userId', '==', userId],
        ['context.cardId', '==', contextCardId]
      ];
      break;
    case 'BOARD':
      whereClause = [
        ['userId', '==', userId],
        ['context.boardId', '==', contextBoardId],
        ['context.cardId', '==', null]
      ];
      break;
    case 'HOME':
    case 'ONBOARDING':
    default:
      whereClause = [
        ['userId', '==', userId],
        ['context.type', 'in', ['HOME', 'ONBOARDING']]
      ];
      break;
  }

  // Dispatch live Firestore query
  try {
    const query = firestoreRedux.query('ai-chats', {
      id: queryId,
      where: whereClause,
      orderBy: [['latestMessageSentAt', 'desc']],
      requesterId: queryId
    });
    yield query.result;
  } catch (error) {
    console.error('Failed to prefetch recent chat:', error);
  }

  previousRecentChatQueryId = queryId;
}

/**
 * Loads quick actions from Firestore based on context type.
 *
 * - Default quick actions (contextTypes == null or []): Loaded once at first call, stays as live listener.
 * - Context-specific quick actions: Loaded using array-contains on contextTypes, previous query cancelled on context change.
 */
function* loadQuickActionsSaga() {
  const state = yield select();
  const context = selectors.pageContext(state);
  if (!context) return;

  const { type: contextType } = context;

  // Load default quick actions (contextTypes is null or empty array) once - always available
  if (!defaultQuickActionsLoaded) {
    defaultQuickActionsLoaded = true;
    try {
      // Query for contextTypes == null
      const queryNull = firestoreRedux.query('ai-quick-actions', {
        id: 'ai-quick-actions-default-null',
        where: [
          ['contextTypes', '==', null],
          ['disabled', '==', false]
        ]
      });
      // Query for contextTypes == [] (empty array)
      const queryEmpty = firestoreRedux.query('ai-quick-actions', {
        id: 'ai-quick-actions-default-empty',
        where: [
          ['contextTypes', '==', []],
          ['disabled', '==', false]
        ]
      });
      yield Promise.all([queryNull.result, queryEmpty.result]);
    } catch (error) {
      console.error('Failed to load default quick actions:', error);
    }
  }

  // Load context-specific quick actions
  const quickActionsQueryId = `ai-quick-actions-${contextType}`;
  if (quickActionsQueryId === previousQuickActionsQueryId) return;

  // Cancel previous context-specific query
  if (previousQuickActionsQueryId) {
    firestoreRedux.cancelQueryByRequester(previousQuickActionsQueryId);
  }

  try {
    const query = firestoreRedux.query('ai-quick-actions', {
      id: quickActionsQueryId,
      requesterId: quickActionsQueryId,
      where: [
        ['contextTypes', 'array-contains', contextType],
        ['disabled', '==', false]
      ]
    });
    yield query.result;
  } catch (error) {
    console.error('Failed to load context-specific quick actions:', error);
  }

  previousQuickActionsQueryId = quickActionsQueryId;
}

/**
 * Opens AI Chat by updating URL query params.
 *
 * Reads prefetched recent chat data (from watchRouterSaga) via recentChatInContext selector.
 * If a recent chat is found within the recency threshold, opens with that chatId.
 * Otherwise, opens as a new session (welcome screen).
 *
 * Flow:
 * 1. Read recent chat from selector (already prefetched by watchRouterSaga)
 * 2. If recent chat found: Update URL with chatId (?aichat={chatId} or ?card-aichat={chatId})
 * 3. If no recent chat: Update URL for new session (?aichat=new or ?card-aichat=new)
 *
 * Note: No Redux state is updated - state is derived from URL.
 */
function* openAiChatSaga(action) {
  const { contextType, boardId, cardId } = action;

  const state = yield select();
  const recentChat = selectors.recentChatInContext(state, contextType, boardId, cardId);
  const newChatDraft = selectors.newChatDraft(state, contextType, boardId, cardId);

  analytics.logAiHelperEvent('ai helper opened', {
    display_mode: contextType === 'CARD' ? 'DIALOG' : 'PANEL',
    trigger: 'FAB',
    context_type: contextType,
    ...(boardId && { context_board_id: boardId }),
    ...(cardId && { context_card_id: cardId }),
    ...(recentChat?.id && { chat_id: recentChat.id })
  });

  // Open AI chat based on context type
  // CARD context uses replace (push=false) so closing the card doesn't leave an extra history entry
  const isCard = contextType === 'CARD';
  const openAiChat = isCard ? router.openDialogAiChat : router.openPageAiChat;

  if (recentChat && !newChatDraft) {
    // Recent chat found and no pending new-chat draft - open with chatId
    yield call(openAiChat, recentChat.id, !isCard);
  } else {
    // No recent chat, or a new-chat draft exists - open as new session
    yield call(openAiChat, null, !isCard);
  }
}

/**
 * Closes AI Chat by clearing URL query params.
 *
 * Flow:
 * 1. Clear URL query params based on context type
 * 2. Firestore cleanup is handled by watchRouterSaga when URL changes
 *
 * Note: Drafts are NOT cleared when closing chat - they are preserved for next session.
 */
function* closeAiChatSaga() {
  const state = yield select();
  const currentContext = selectors.currentContext(state);
  const isCard = currentContext?.type === 'CARD';
  const chatId = isCard ? routerSelectors.dialogAiChatId(state) : routerSelectors.pageAiChatId(state);

  analytics.logAiHelperEvent('ai helper closed', {
    context_type: currentContext?.type,
    ...(currentContext?.boardId && { context_board_id: currentContext.boardId }),
    ...(currentContext?.cardId && { context_card_id: currentContext.cardId }),
    ...(chatId && { chat_id: chatId })
  });

  // Close AI chat based on context type
  const closeAiChat = isCard ? router.closeDialogAiChat : router.closePageAiChat;
  yield call(closeAiChat, false); // replace, not push
}

/**
 * Updates draft message in Redux state.
 *
 * Flow:
 * 1. Check if chatId is provided
 * 2. If chatId exists: Update draftMessages[chatId]
 * 3. If chatId doesn't exist: Get current context, generate context key, update newChatDrafts[contextKey]
 * 4. persistEnhancer automatically saves to localStorage
 */
function* updateDraftMessageSaga(action) {
  const { draftText, chatId } = action;

  const state = yield select();
  const userId = auth.selectors.currentUserId(state);
  if (!userId) return;

  if (chatId) {
    // Existing chat - update draftMessages[userId][chatId]
    yield put({
      type: actions.SET_DRAFT_MESSAGE,
      chatId,
      draftText,
      userId
    });
  } else {
    // New chat - get current context and update newChatDrafts[userId][contextKey]
    const currentContext = selectors.currentContext(state);

    if (!currentContext || !currentContext.type) {
      console.warn('Cannot save new chat draft: currentContext is not available');
      return;
    }

    yield put({
      type: actions.SET_NEW_CHAT_DRAFT,
      contextType: currentContext.type,
      boardId: currentContext.boardId,
      cardId: currentContext.cardId,
      draftText,
      userId
    });
  }
}

/**
 * Loads chat data by setting up Firestore listeners for chat document and messages.
 *
 * Currently used when a new chat is created (via startNewChatSaga).
 * This is the common function that will be reused when implementing:
 * - Opening existing chat from history
 * - Opening chat directly via URL
 * - Auto-resuming recent chat
 *
 * Firestore Structure:
 * - Chat document: ai-chats/{chatId}
 * - Messages subcollection: ai-chats/{chatId}/ai-messages/{messageId}
 * - Tool messages subcollection: ai-chats/{chatId}/ai-tool-messages/{toolMessageId}
 *
 * Security Rules:
 * - Chat owner can always read their chats
 * - Shared chats (shared=true) are readable by board/card team members
 * - See wiki/redux/ai-chat-rules.md for details
 *
 * Required Indexes:
 * - No composite index needed for basic message loading (single orderBy on subcollection)
 * - See wiki/redux/ai-chat-indexes.md for all indexes (needed for future features)
 *
 * @param {String} chatId - ID of the chat to load
 */
function* loadChatDataSaga(chatId) {
  if (!chatId) {
    console.warn('Cannot load chat data: chatId is required');
    return;
  }

  yield spawn(loadAiChatSaga, chatId);
  yield spawn(loadAiMessagesSaga, chatId);
  yield spawn(loadToolMessagesSaga, chatId);
  yield put(attachments.aichat.actions.load(chatId));
}

/**
 * Loads AI chat document from Firestore.
 *
 * @param {String} chatId - ID of the chat to load
 */
function* loadAiChatSaga(chatId) {
  if (!chatId) {
    console.warn('Cannot load AI chat: chatId is required');
    return;
  }

  try {
    // Load chat document with retry for permission errors
    yield call(() => retry(() => {
      const query = firestoreRedux.getDocById('ai-chats', chatId, {
        requesterId: `ai-chat-${chatId}`,
      });
      return query.result;
    }, { maxAttempts: 3, factor: 2, delay: 2000, maxDelay: 5000 }));
  } catch (error) {
    const errorText = String(error || '');
    if (errorText.includes('permission-denied') || errorText.includes('Missing or insufficient permissions')) {
      yield put({ type: actions.SET_CHAT_ACCESS_DENIED, chatId });
    } else {
      console.error('Failed to load AI chat:', error);
    }
  }
}

/**
 * Loads AI messages for a chat from Firestore.
 *
 * @param {String} chatId - ID of the chat to load messages for
 */
function* loadAiMessagesSaga(chatId) {
  if (!chatId) {
    console.warn('Cannot load AI messages: chatId is required');
    return;
  }

  try {
    // Load messages with retry for permission errors
    yield call(() => retry(() => {
      const query = firestoreRedux.query('ai-messages', {
        id: `ai-messages-${chatId}`,
        where: [['chatId', '==', chatId]],
        orderBy: [['createdAt', 'asc']],
        requesterId: `ai-messages-${chatId}`,
      });
      return query.result;
    }, { maxAttempts: 3, factor: 2, delay: 2000, maxDelay: 5000 }));
  } catch (error) {
    console.error('Failed to load AI messages:', error);
  }
}

/**
 * Loads AI tool messages for a chat from Firestore.
 *
 * @param {String} chatId - ID of the chat to load tool messages for
 */
function* loadToolMessagesSaga(chatId) {
  if (!chatId) {
    console.warn('Cannot load tool messages: chatId is required');
    return;
  }

  try {
    // Load tool messages with retry for permission errors
    yield call(() => retry(() => {
      const query = firestoreRedux.query('ai-tool-messages', {
        id: `ai-tool-messages-${chatId}`,
        where: [['chatId', '==', chatId]],
        orderBy: [['createdAt', 'asc']],
        requesterId: `ai-tool-messages-${chatId}`,
      });
      return query.result;
    }, { maxAttempts: 3, factor: 2, delay: 2000, maxDelay: 5000 }));
  } catch (error) {
    console.error('Failed to load tool messages:', error);
  }
}

/**
 * Creates a new AI chat with initial message.
 *
 * This saga uses localWrite pattern for instant UI feedback:
 * 1. Generates local IDs and creates local chat/message documents (user sees quick response)
 * 2. Calls API to create new chat with first message
 * 3. On success: Clears new chat draft, server updates Firestore
 * 4. On error: Deletes local documents and restores draft
 */
/**
 * Board-state signals sent with a board chat's message; the server derives the card set from them.
 * A scope picked in the `@` picker does not change them — it reaches the model as a mention in the prompt.
 */
const boardStateContext = (state) => {
  // Only the highlights criteria is sent; the server resolves which cards it highlights.
  const highlightCriteria = boardHighlightsSelectors.highlightCriteria(state);
  return {
    selectedCardIds: boardSelectors.selectedCards(state) || [],
    ...(highlightCriteria && { highlightCriteria }),
    hiddenColumnIds: boardSelectors.hiddenColumnIds(state) || [],
  };
};

function* startNewChatSaga(action) {
  const { context, request } = action;
  const baseRequest = { ...request };

  // Generate local IDs for optimistic update
  let chatId = null;
  let messageId = null;

  // Extract userPrompt for draft restoration on error; may be refreshed after upload wait
  let userPrompt = get(request, 'userPrompt', '');

  try {
    // Get current user ID for local write
    const state = yield select();
    const userId = auth.selectors.currentUserId(state);

    // Generate local IDs
    chatId =  context.chatId || `aic_${entityIdProvider.getId()}`;
    messageId = `aicm_${entityIdProvider.getId()}`;

    // Query IDs must match the actual query IDs used in loadChatDataSaga
    const chatQueryId = `ai-chat-${chatId}`;
    const messagesQueryId = `ai-messages-${chatId}`;

    const now = Date.now();

    // Prepare context for API and local write
    const apiContext = {
      type: context.type,
      ...(context.boardId && { boardId: context.boardId }),
      ...(context.cardId && { cardId: context.cardId })
    };

    // Create local chat document for instant UI feedback
    const chatDoc = {
      id: chatId,
      userId,
      context: apiContext,
      createdAt: now,
      latestMessageSentAt: now,
      creating: true // Flag to indicate local write in progress
    };

    const tools = [{ name: 'webSearch', enabled: true }];

    const references = request.references || [];

    // If component signaled uploads are in progress, wait for them before submitting
    if (request.hasUploadsInProgress) {
      const contextKey = buildContextKey(context.type, context.boardId, context.cardId);
      yield put(actions._setAutoSubmit(contextKey));
      const result = yield call(waitForUploads, references, contextKey);
      if (result === 'CANCELED') return; // user stopped — state already cleared
      yield put(actions.clearAutoSubmit(contextKey));
      if (result !== 'SUCCESS') return;
      // Re-read fresh prompt from draft — user may have edited text while waiting
      const freshState = yield select();
      const freshPrompt = selectors.newChatDraft(freshState, context.type, context.boardId, context.cardId);
      if (freshPrompt !== null) {
        userPrompt = freshPrompt;
      }
    }
    const markdownPrompt = yield call(promptToMarkdown, userPrompt);

    // Board-state signals for board chats (before local write so they're available for instant
    // display).
    const implicitContextEnabled = feature.selectors.isAvailable(state, 'ai-chat-implicit-context');
    const implicitContext = implicitContextEnabled && context.type === 'BOARD' ? boardStateContext(state) : undefined;

    // What the user has written on the page and not saved, so the assistant can work from text the
    // server has never seen. Read from the page rather than from `context`, which carries only which
    // card or board the chat belongs to.
    const requestContext = yield call(_requestContext, get(selectors.pageContext(state), 'draft'), implicitContext);

    // Create local message document for instant UI feedback
    const messageDoc = {
      id: messageId,
      chatId: chatId,
      userId,
      initial: true,
      status: 'GENERATING',
      statusAt: now,
      createdAt: now,
      request: {
        createdAt: now,
        userPrompt: markdownPrompt,
        tools,
        references,
        ...(requestContext && { context: requestContext }),
      },
      responses: [],
      creating: true // Flag to indicate local write in progress
    };

    // Local write - user sees instant response
    firestoreRedux.save('ai-chats', chatDoc, { localWrite: true, queryId: chatQueryId });
    firestoreRedux.save('ai-messages', messageDoc, { localWrite: true, queryId: messagesQueryId });

    analytics.logAiHelperEvent('ai message submitted', {
      chat_id: chatId,
      message_id: messageId,
      is_init: true,
      context_type: context.type,
      ...(context.boardId && { context_board_id: context.boardId }),
      ...(context.cardId && { context_card_id: context.cardId })
    });

    // Clear new chat draft optimistically
    yield put(actions.clearNewChatDraft(context.type, context.boardId, context.cardId, userId));

    // Update URL to show the new chat based on context
    if (context.type === 'CARD') {
      yield call(router.setDialogAiChatId, chatId, true); // replace URL
    } else {
      yield call(router.setPageAiChatId, chatId, true); // replace URL
    }

    const apiRequest = {
      ...baseRequest,
      userPrompt: markdownPrompt,
      tools,
      ...(requestContext && { context: requestContext }),
    };

    // Build request body
    const requestBody = {
      chatId: chatId, // Send local ID to server so it uses the same ID
      messageId: messageId,
      context: apiContext,
      request: apiRequest,
    };

    // Call API to create new chat
    yield call(requestApi, '/ai/chats', {
      method: 'POST',
      body: requestBody
    });

    // Load chat data (server will update the documents via Firestore)
    yield call(loadChatDataSaga, chatId);

  } catch (error) {
    console.error('Failed to start new chat:', error);

    // Delete local documents on error
    if (chatId) {
      const chatQueryId = `ai-chat-${chatId}`;
      const messagesQueryId = `ai-messages-${chatId}`;
      firestoreRedux.delete('ai-chats', chatId, { localWrite: true, queryId: chatQueryId });
      if (messageId) {
        firestoreRedux.delete('ai-messages', messageId, { localWrite: true, queryId: messagesQueryId });
      }
    }

    // Restore draft message
    if (userPrompt) {
      yield put(actions.updateDraftMessage(userPrompt, null));
    }

    // Update URL back to new chat (remove chatId from URL)
    if (context.type === 'CARD') {
      yield call(router.openDialogAiChat, null, false); // set ?card-aichat=new
    } else {
      yield call(router.openPageAiChat, null, false); // set ?aichat=new
    }

    showSnackBar({
      type: 'ERROR',
      message: i18next.t('ai-chat:toast.failedToStartChat')
    });
  }
}

/**
 * Handles entering or exiting edit mode for a message.
 *
 * When messageId is null (cancel edit):
 * - Clears the editing state for the chat
 * - Clears the draft message
 *
 * When messageId is set (enter edit mode):
 * 1. Gets the message from state
 * 2. If message not found, logs error and returns
 * 3. Extracts the user prompt from the message
 * 4. Sets the editing state for the chat
 * 5. Pre-fills the draft message with the original text
 *
 * Note: If the message is still GENERATING when the user re-submits, cancellation
 * happens in sendMessageSaga — not here.
 *
 * @param {Object} action - Redux action with chatId and messageId
 */
function* editMessageSaga(action) {
  const { chatId, messageId } = action;

  // Toggle edit mode only. The unsent draft in `draftMessages` is left untouched — the input shows
  // the edited message's prompt while editing and restores the draft on exit; keystrokes during
  // edit don't write to `draftMessages` (see ai-chat-input `_handleTextChange`).
  if (!messageId) {
    yield put(actions.setEditMessage(chatId, null));
    return;
  }

  const state = yield select();
  const msg = selectors.message(state, chatId, messageId);
  if (!msg) {
    console.error('Message not found', messageId);
    return;
  }
  yield put(actions.setEditMessage(chatId, messageId));
}

/**
 * Sends a message in an existing chat with validation and error handling.
 *
 * This saga uses localWrite pattern for instant UI feedback:
 * 1. Validates no concurrent request in same chat
 * 2. Validates global concurrent request limit
 * 3. Creates local message document (user sees quick response)
 * 4. Clears draft message optimistically
 * 5. If editing: Calls edit API, else calls send API
 * 6. On success: Exits edit mode if applicable, server updates Firestore
 * 7. On failure: Deletes local message, restores draft, shows error toast
 */
function* sendMessageSaga(action) {
  const { chatId, request } = action;
  const requestBody = { ...request };

  // Extract userPrompt for draft restoration; may be refreshed after upload wait
  let userPrompt = get(request, 'userPrompt', '');

  // Generate local message ID for optimistic update
  let messageId = null;
  const messagesQueryId = `ai-messages-${chatId}`;

  // Step 1: Validate no concurrent request in same chat
  const state = yield select();
  const hasInProgress = selectors.hasInProgressMessage(state, chatId);

  if (hasInProgress) {
    console.error('Cannot send message: A request is already in progress for this chat', { chatId });
    showSnackBar({
      type: 'ERROR',
      message: i18next.t('ai-chat:toast.requestInProgress')
    });
    return; // Do not proceed
  }

  // Step 2: Validate global concurrent request limit
  const concurrentCount = selectors.concurrentRequestCount(state);
  const concurrentLimit = 5; // Default limit as per documentation

  if (concurrentCount >= concurrentLimit) {
    console.error('Cannot send message: Concurrent request limit exceeded', { concurrentCount, concurrentLimit });
    showSnackBar({
      type: 'ERROR',
      message: i18next.t('ai-chat:toast.concurrentLimitExceeded')
    });
    return; // Do not proceed
  }

  // Check if we're editing a message
  const editMessageId = selectors.editMessage(state, chatId);
  const tools = [{ name: 'webSearch', enabled: true }];

  // Get the original message doc now (before any local write) for rollback
  const originalEditMessage = editMessageId ? selectors.message(state, chatId, editMessageId) : null;

  // Get current user ID for local write
  const userId = auth.selectors.currentUserId(state);

  // Get the last message to set previousMessageId for the chain
  const lastMsg = selectors.lastMessage(state, chatId);
  const previousMessageId = lastMsg ? lastMsg.id : null;

  // Generate local message ID
  messageId = `aicm_${entityIdProvider.getId()}`;

  const now = Date.now();
  const references = request.references || [];

  // If component signaled uploads are in progress, wait for them before submitting
  if (request.hasUploadsInProgress) {
    yield put(actions._setAutoSubmit(chatId));
    const result = yield call(waitForUploads, references, chatId);
    if (result === 'CANCELED') return; // user stopped — state already cleared
    yield put(actions.clearAutoSubmit(chatId));
    if (result !== 'SUCCESS') return;
    // Re-read fresh prompt — user may have edited text while waiting
    const freshState = yield select();
    const freshDraft = selectors.draftMessage(freshState, chatId);
    if (freshDraft !== null) {
      userPrompt = freshDraft;
    }
  }

  const currentCtx = selectors.currentContext(state);
  const markdownPrompt = yield call(promptToMarkdown, userPrompt);

  // Board-state signals for board chats (before local write so they're available for instant
  // display).
  const implicitContextEnabled = feature.selectors.isAvailable(state, 'ai-chat-implicit-context');
  const implicitContext = implicitContextEnabled && currentCtx?.type === 'BOARD' ? boardStateContext(state) : undefined;

  // Sent with every turn, not only the first: the user may write the description and then ask about
  // it, and each turn has to carry the draft as it stands then.
  const requestContext = yield call(_requestContext, get(selectors.pageContext(state), 'draft'), implicitContext);

  // Create local message document for instant UI feedback
  const messageDoc = {
    id: messageId,
    chatId,
    userId,
    previousMessageId,
    status: 'GENERATING',
    statusAt: now,
    createdAt: now,
    request: {
      createdAt: now,
      userPrompt: markdownPrompt,
      tools,
      references,
      ...(requestContext && { context: requestContext }),
    },
    responses: [],
    creating: true // Flag to indicate local write in progress
  };

  // Local write - user sees instant response
  firestoreRedux.save('ai-messages', messageDoc, { localWrite: true, queryId: messagesQueryId });

  // If editing, optimistically hide the original message by setting editedToMessageId
  // so it's filtered out immediately (prevents showing both old and new message)
  if (originalEditMessage) {
    firestoreRedux.save('ai-messages', { ...originalEditMessage, editedToMessageId: messageId }, { localWrite: true, queryId: messagesQueryId });
  }

  analytics.logAiHelperEvent('ai message submitted', {
    chat_id: chatId,
    message_id: messageId,
    is_init: false,
    context_type: currentCtx?.type,
    ...(currentCtx?.boardId && { context_board_id: currentCtx.boardId }),
    ...(currentCtx?.cardId && { context_card_id: currentCtx.cardId })
  });

  // Step 3: Optimistically clear draft message (before API call). Not for an edit — there the
  // draft is the untouched unsent input, and the edited text lives only in the editor.
  if (!editMessageId) {
    yield put(actions.clearDraftMessage(chatId, userId));
  }

  try {
    if (editMessageId) {
      // Edit flow - create new message from edited prompt
      yield call(requestApi, `/ai/chats/${chatId}/messages/${editMessageId}`, {
        method: 'POST',
        body: { ...requestBody, userPrompt: markdownPrompt, tools, messageId: messageId, ...(requestContext && { context: requestContext }) },
        excludeErrors: [409]
      });

      // Exit edit mode on success — the input then restores the unsent draft (untouched during edit).
      yield put(actions.setEditMessage(chatId, null));
    } else {
      // New message flow
      yield call(requestApi, `/ai/chats/${chatId}/messages`, {
        method: 'POST',
        body: { ...requestBody, userPrompt: markdownPrompt, tools, messageId: messageId, ...(requestContext && { context: requestContext }) },
        excludeErrors: [409]
      });
    }

    // Server will update the message document via Firestore
    // Local write will be replaced by server data

  } catch (error) {
    console.error('Failed to send message:', error);

    // Delete local message on error
    if (messageId) {
      firestoreRedux.delete('ai-messages', messageId, { localWrite: true, queryId: messagesQueryId });
    }

    // Restore original message on error (undo the optimistic hide)
    if (originalEditMessage) {
      firestoreRedux.save('ai-messages', originalEditMessage, { localWrite: true, queryId: messagesQueryId });
    }

    // Restore draft message on failure — only for a new message. For an edit, the unsent draft is
    // untouched and the edited text is still in the editor for retry.
    if (!editMessageId) {
      yield put(actions.updateDraftMessage(userPrompt, chatId));
    }

    // Show error toast based on error type
    const statusCode = get(error, 'status');
    const errorCode = get(error, 'body.code') || get(error, 'body.error');

    // Handle specific 409 Conflict errors — these are expected race conditions
    if (statusCode === 409) {
      if (errorCode === 'CHAT_HAS_GENERATING_MESSAGE') {
        showSnackBar({
          message: i18next.t('ai-chat:toast.chatHasGeneratingMessage')
        });
      } else if (errorCode === 'CONCURRENT_MESSAGE_LIMIT_EXCEEDED') {
        showSnackBar({
          type: 'ERROR',
          message: i18next.t('ai-chat:toast.concurrentLimitExceeded')
        });
      } else if (errorCode === 'CHAT_CREATION_IN_PROGRESS') {
        showSnackBar({
          type: 'ERROR',
          message: i18next.t('ai-chat:toast.chatCreationInProgress')
        });
      } else {
        // Generic 409 error
        showSnackBar({
          type: 'ERROR',
          message: i18next.t('ai-chat:toast.unableToSendMessage')
        });
      }
    } else {
      // Generic error for non-409 status codes
      showSnackBar({
        type: 'ERROR',
        message: i18next.t('ai-chat:toast.failedToSendMessage')
      });
    }

    // Keep edit mode intact on error so user can retry
  }
}

/**
 * Starts a new chat session without creating a chat yet.
 *
 * Flow:
 * 1. Sets URL to `?aichat=new` or `?card-aichat=new`, removing `?show-chat-history` in the same step
 * 2. Shows welcome screen (handled by UI based on isNewChat flag from URL)
 *
 * Note: New chat drafts are preserved and will be shown in the new session.
 */
function* startNewChatSessionSaga() {
  const state = yield select();
  const currentContext = selectors.currentContext(state);

  const isCard = currentContext?.type === 'CARD';
  const openAiChat = isCard ? router.openDialogAiChat : router.openPageAiChat;
  yield call(openAiChat, null, !isCard); // CARD replaces, BOARD pushes
}

/**
 * Cancels an in-progress message generation.
 *
 * Flow:
 * 1. Local writes message status to 'CANCELLED' for instant UI feedback
 * 2. Calls server API to cancel the message: POST /ai/chats/{chatId}/messages/{messageId}/cancel
 * 3. Server processes cancellation and updates Firestore
 * 4. Firestore snapshot replaces local write with server data
 *
 * Error Handling:
 * - If API call fails, logs error (server data will eventually sync)
 * - No user-facing error needed - they can retry cancel or wait for completion
 *
 * Interaction with Other Features:
 * - Edit Message: If user clicks edit while message is generating, editMessageSaga calls this internally
 * - Regenerate: Cancelled messages can be regenerated via regenerateResponse action
 * - Start New Chat: Cancelling doesn't affect chat session - chat remains active
 *
 * @param {Object} action - Redux action with chatId and messageId
 */
function* cancelMessageSaga(action) {
  const { chatId, messageId } = action;

  if (!chatId || !messageId) {
    console.warn('Cannot cancel message: chatId and messageId are required');
    return;
  }

  const messagesQueryId = `ai-messages-${chatId}`;
  const chatQueryId = `ai-chat-${chatId}`;
  const state = yield select();
  const currentMessage = selectors.message(state, chatId, messageId);
  const currentChat = selectors.chat(state, chatId);
  // The chat mirrors only its latest message, which is the one still generating.
  const generatingChat = currentChat?.lastMessageStatus === 'GENERATING' ? currentChat : null;

  // Skip if message is already in a final state
  const messageStatus = currentMessage?.status;
  if (messageStatus === 'COMPLETED' || messageStatus === 'ERROR' || messageStatus === 'CANCELLED') {
    return;
  }

  try {
    // Local write to show instant UI feedback (same pattern as attachment upload cancel)
    if (currentMessage) {
      const statusAt = Date.now();
      const cancelledMessage = {
        ...currentMessage,
        status: 'CANCELLED',
        statusAt,
        // The response keeps its own status, which wins over the message's.
        responses: currentMessage.responses?.map((response) =>
          response.status === 'GENERATING' || response.status === 'WAITING_FOR_UPLOAD'
            ? { ...response, status: 'CANCELLED', statusAt }
            : response
        ),
      };
      firestoreRedux.save('ai-messages', cancelledMessage, { localWrite: true, queryId: messagesQueryId });
      if (generatingChat) {
        firestoreRedux.save('ai-chats', {
          ...generatingChat,
          lastMessageStatus: 'CANCELLED',
          lastMessageStatusAt: statusAt,
        }, { localWrite: true, queryId: chatQueryId });
      }
    }

    yield call(requestApi, `/ai/chats/${chatId}/messages/${messageId}/cancel`, {
      method: 'POST',
      excludeErrors: [409]
    });
    yield put({ type: actions.CANCEL_MESSAGE_SUCCESS, chatId, messageId });
  } catch (error) {
    // 409: the server had already finished the message, so there is nothing to cancel. Its
    // final state arrives through the snapshot; restoring the GENERATING copy taken before the
    // click could land on top of that snapshot and stay there until reload.
    if (error?.status === 409) {
      yield put({ type: actions.CANCEL_MESSAGE_FAILED, chatId, messageId });
      return;
    }
    console.error('Failed to cancel message:', error);

    // Restore previous state on error
    if (currentMessage) {
      firestoreRedux.save('ai-messages', currentMessage, { localWrite: true, queryId: messagesQueryId });
    }
    if (generatingChat) {
      firestoreRedux.save('ai-chats', generatingChat, { localWrite: true, queryId: chatQueryId });
    }
    yield put({ type: actions.CANCEL_MESSAGE_FAILED, chatId, messageId });
  }
}

/**
 * Starts a new AI Chat using a quick action.
 *
 * This saga uses localWrite pattern for instant UI feedback:
 * 1. Reads quick action from prefetched data in Redux state (loaded at saga startup)
 * 2. Shows toast error if quick action is unavailable or disabled
 * 3. Generates local IDs and creates local chat/message documents (user sees quick response)
 * 4. Calls POST /ai/chats API with context and quick action data
 * 5. On error, deletes the local documents
 *
 * @param {Object} action - Redux action
 * @param {String} action.quickActionName - Name of the quick action (e.g., 'MAKE_BOARD_LIKE_THIS', 'CREATE_BOARD')
 * @param {Object} action.context - Context object
 * @param {String} action.context.type - Context type: 'BOARD' | 'HOME' | 'ONBOARDING'
 * @param {String} [action.context.boardId] - Board ID (required when type is 'BOARD')
 * @param {String} [action.context.cardId] - Card ID (optional)
 */
/**
 * Common saga for starting a new AI chat with a quick action.
 * Handles local optimistic write, API call, and error recovery.
 *
 * @param {Object} quickAction - Quick action object from Firestore
 * @param {Object} context - Context object with type, boardId, cardId
 */
function* _startNewChatWithQuickActionSaga(quickAction, context, chatAttachments) {
  let chatId = null;
  let messageId = null;

  try {
    const state = yield select();
    const { id, title, icon, prompt, preloadSkills } = quickAction;
    const tools = [{ name: 'webSearch', enabled: true }]; // Web search tool is enabled for all quick actions

    // A quick action has no conversation behind it, so the first turn is the only chance to carry
    // what the user has written and not saved. The caller's own draft wins: Redux knows only what the
    // user has *changed*, so a description opened but not typed in has none of its own, and the field
    // passes what its editor is holding.
    const requestContext = yield call(_requestContext, get(context, 'draft') || get(selectors.pageContext(state), 'draft'));
    // Only what the action itself carries in Firestore: a context's own skills are not the user's ask.
    const preLoadSkills = preloadSkills || [];

    const resolvedTitle = resolveTemplate(title, {});
    const resolvedPrompt = resolveTemplate(prompt, {});
    const userId = auth.selectors.currentUserId(state);

    chatId = chatAttachments ? chatAttachments.chatId : `aic_${entityIdProvider.getId()}`;
    messageId = `aicm_${entityIdProvider.getId()}`;

    const chatQueryId = `ai-chat-${chatId}`;
    const messagesQueryId = `ai-messages-${chatId}`;
    const now = Date.now();

    const apiContext = {
      type: context.type,
      ...(context.boardId && { boardId: context.boardId }),
      ...(context.cardId && { cardId: context.cardId })
    };

    // Build references and userPrompt for pre-uploaded attachments
    let userPrompt = resolvedPrompt;
    let references;

    if (chatAttachments) {
      references = chatAttachments.attachmentIds.map(id => ({ type: 'FILE', id }));
      userPrompt = yield call(
        promptToMarkdown,
        chatAttachments.attachmentIds
          .map((attachmentId) => `<a data-type="attachment" data-id="${escapeHtml(attachmentId)}">${escapeHtml(attachments.aichat.selectors.attachment(state, attachmentId)?.title || '')}</a>`)
          .join(' ')
      );
    }

    // Local write — user sees instant response
    firestoreRedux.save('ai-chats', {
      id: chatId, userId, context: apiContext, createdAt: now, latestMessageSentAt: now, creating: true
    }, { localWrite: true, queryId: chatQueryId });

    firestoreRedux.save('ai-messages', {
      id: messageId, chatId, userId, initial: true, status: 'GENERATING', statusAt: now, createdAt: now,
      request: {
        createdAt: now,
        userPrompt,
        tools,
        quickAction: { id, title: resolvedTitle, icon },
        ...(references && { references }),
        ...(requestContext && { context: requestContext }),
        ...(!isEmpty(preLoadSkills) && { preLoadSkills }),
      },
      responses: [],
      creating: true
    }, { localWrite: true, queryId: messagesQueryId });

    if (!chatAttachments) {
      analytics.logAiHelperEvent('ai message submitted', {
        chat_id: chatId,
        message_id: messageId,
        is_init: true,
        context_type: context.type,
        ...(context.boardId && { context_board_id: context.boardId }),
        ...(context.cardId && { context_card_id: context.cardId }),
        submit_type: 'QUICK_ACTION',
        quick_action_id: id,
        quick_action_title: resolvedTitle
      });
    }

    // Opening the panel clears the page's `action`, which closes whatever dialog was open. The board
    // chat is the exception: the quick action was started from inside it, and closing it took the
    // user out of the very thread they were polishing.
    const startedFromBoardChat = get(routerSelectors.page(state), 'params.action') === 'board-chat';
    if (context.type === 'CARD') {
      yield call(router.setDialogAiChatId, chatId, true, true);
    } else {
      yield call(router.setPageAiChatId, chatId, true, !startedFromBoardChat);
    }

    yield call(requestApi, '/ai/chats', {
      method: 'POST',
      body: {
        chatId,
        messageId,
        context: apiContext,
        request: {
          userPrompt: resolvedPrompt,
          tools,
          quickAction: { id, title: resolvedTitle, icon },
          ...(references && { references }),
          ...(requestContext && { context: requestContext }),
          ...(!isEmpty(preLoadSkills) && { preLoadSkills }),
        }
      }
    });

    yield call(loadChatDataSaga, chatId);

  } catch (error) {
    console.error('Failed to start AI chat with quick action:', error);

    if (chatId) {
      const chatQueryId = `ai-chat-${chatId}`;
      const messagesQueryId = `ai-messages-${chatId}`;
      firestoreRedux.delete('ai-chats', chatId, { localWrite: true, queryId: chatQueryId });
      if (messageId) {
        firestoreRedux.delete('ai-messages', messageId, { localWrite: true, queryId: messagesQueryId });
      }
    }

    if (context.type === 'CARD') {
      yield call(router.openDialogAiChat, null, false);
    } else {
      yield call(router.openPageAiChat, null, false);
    }

    showSnackBar({ type: 'ERROR', message: i18next.t('ai-chat:toast.failedToStartChat') });
  }
}


/**
 * Starts a new AI chat using a quick action looked up by name.
 * Used by external triggers (e.g., "Make a board like this" button).
 */
function* startNewChatUsingQuickActionByNameSaga(action) {
  const { quickActionName, context, chatAttachments } = action;

  const state = yield select();
  const quickAction = selectors.quickActionByName(state, quickActionName);

  if (!quickAction) {
    console.error('Quick action not found:', { quickActionName, contextType: context.type });
    showSnackBar({
      type: 'ERROR',
      message: i18next.t('ai-chat:toast.actionUnavailable')
    });
    return;
  }

  const triggerMap = {
    'CREATE_BOARD': 'CREATE_BOARD_WITH_AI',
    'CREATE_TEMPLATE': 'CREATE_TEMPLATE_WITH_AI',
    'MAKE_BOARD_LIKE_THIS': 'MAKE_BOARD_LIKE_THIS',
  };
  analytics.logAiHelperEvent('ai helper opened', {
    display_mode: context.type === 'CARD' ? 'DIALOG' : 'PANEL',
    trigger: triggerMap[quickActionName] || (isQuickActionAllowed(quickAction, { trigger: 'LIST' }) ? 'FAB' : quickActionName),
    context_type: context.type,
    ...(context.boardId && { context_board_id: context.boardId }),
    ...(context.cardId && { context_card_id: context.cardId })
  });

  yield call(_startNewChatWithQuickActionSaga, quickAction, context, chatAttachments);
}

/**
 * Starts a new AI chat using a quick action looked up by ID.
 * Used by the quick action item component when user clicks a quick action button.
 */
function* startNewChatUsingQuickActionSaga(action) {
  const { quickActionId, context } = action;

  const state = yield select();
  const quickAction = selectors.quickAction(state, quickActionId);

  if (!quickAction) {
    console.error('Quick action not found:', { quickActionId, contextType: context.type });
    showSnackBar({
      type: 'ERROR',
      message: i18next.t('ai-chat:toast.actionUnavailable')
    });
    return;
  }

  yield call(_startNewChatWithQuickActionSaga, quickAction, context);
}

/**
 * Watches for board creation via AI Chat and navigates to the new board.
 *
 * This saga listens to Firestore snapshots and handles two events:
 * 1. Tool status changes to SUCCESS (from ai-tool-messages) - stores tool data
 * 2. Message status changes to COMPLETED (from ai-messages) - dispatches clone action
 *
 * The clone action is only dispatched when BOTH tool and message status are COMPLETED.
 * Since tool status changes first, we store the tool data and wait for message COMPLETED.
 *
 * Performance: Works directly with action data to avoid unnecessary selector calls.
 * Only accesses Redux state when navigation is actually needed.
 *
 * @param {Object} action - Firestore QUERY_SNAPSHOT action
 */
function* boardCreatedFromChatSaga(action) {
  // Handle both in parallel using spawn
  yield spawn(_handleToolStatusChange, action);
  yield spawn(_handleMessageStatusChange, action);
}

/**
 * Handles tool status change from ai-tool-messages snapshot.
 * When createNewBoard tool becomes SUCCESS, checks if message is also COMPLETED.
 * If yes, dispatches clone action immediately. If no, stores tool data for later.
 */
function* _handleToolStatusChange(action) {
  const toolData = yield call(_extractCreateBoardToolFromAction, action);

  if (!toolData) {
    return;
  }

  const { chatId, tool, toolMessageId, messageId } = toolData;

  if (!messageId) {
    console.warn('Tool message missing messageId field');
    return;
  }

  // Track status changes
  const statusKey = `${chatId}_${toolMessageId}_${tool.name}`;
  const previousStatus = previousMessageStatus[statusKey];
  const currentStatus = tool.status;
  previousMessageStatus[statusKey] = currentStatus;

  // Skip if already processed SUCCESS
  if (previousStatus === 'SUCCESS') {
    return;
  }

  // If IN_PROGRESS, store for later processing when message completes
  if (currentStatus === 'IN_PROGRESS') {
    pendingBoardCreationTools[chatId] = { tool, statusKey, messageId, toolMessageId };
    return;
  }

  // Only proceed if currentStatus is SUCCESS
  if (currentStatus !== 'SUCCESS') {
    return;
  }

  // Verify this is still the active chat
  const state = yield select();
  const currentChatId = selectors.currentChatId(state);

  if (!currentChatId || chatId !== currentChatId) {
    return;
  }

  // Check if we're already on the target board - prevents infinite clone loop
  const targetBoardId = tool.output?.boardId;
  const currentBoardId = routerSelectors.pageBoardId(state);
  if (targetBoardId && targetBoardId === currentBoardId) {
    return;
  }

  // Skip replay: only process board creations that belong to the current user and were recent.
  // This prevents re-navigation and duplicate analytics in fresh sessions (e.g. incognito).
  const userId = auth.selectors.currentUserId(state);
  const shouldProcess = yield call(_isBoardRecentlyCreatedByCurrentUser, targetBoardId, userId);
  if (!shouldProcess) return;

  // Check if message status is already COMPLETED using exact messageId
  const msg = selectors.message(state, chatId, messageId);
  if (msg && msg.status === 'COMPLETED') {
    // Message is already COMPLETED, navigate immediately
    yield spawn(_logAiBoardCreatedEventSaga, targetBoardId);
    yield call(_navigateToNewBoardSaga, tool, statusKey, chatId, messageId, toolMessageId);
    return;
  }

  // Store tool data with messageId, waiting for message COMPLETED
  pendingBoardCreationTools[chatId] = { tool, statusKey, messageId, toolMessageId };
}

/**
 * Handles message status change from ai-messages snapshot.
 * When message becomes COMPLETED and there's a createNewBoard tool with SUCCESS status, dispatches clone action.
 *
 * This handler has two modes:
 * 1. Uses pending tool data stored by _handleToolStatusChange (normal case)
 * 2. Reads tool data from state (fallback for when tool snapshot was debounced away)
 */
function* _handleMessageStatusChange() {
  // Always check pending chats and read message status from state (not from action)
  // This ensures we get the latest status, not stale data from the action
  const pendingChatIds = Object.keys(pendingBoardCreationTools);

  if (pendingChatIds.length === 0) {
    return;
  }

  const state = yield select();

  for (const chatId of pendingChatIds) {
    const pendingTool = pendingBoardCreationTools[chatId];
    if (!pendingTool) continue;

    // Verify this is still the active chat
    const currentChatId = selectors.currentChatId(state);
    if (!currentChatId || chatId !== currentChatId) continue;

    // Read message status from state (always fresh)
    const msg = selectors.message(state, chatId, pendingTool.messageId);

    if (msg && msg.status === 'COMPLETED') {
      // Re-read the tool from Redux state to get current status and output
      // A single doc can have multiple matching toolCalls (e.g. FAILED + SUCCESS retry)
      const allToolMessages = selectors.allToolMessages(state, chatId);
      const toolMessage = allToolMessages?.find(tm => tm.id === pendingTool.toolMessageId);
      const matchingTools = toolMessage?.toolCalls?.filter(t => t.name === 'createNewBoard' || t.name === 'cloneBoard') || [];
      const currentTool = matchingTools.find(t => t.status === 'SUCCESS') || matchingTools[matchingTools.length - 1];

      if (currentTool && currentTool.status === 'SUCCESS') {
        yield call(_processCompletedBoardCreation, state, chatId, { ...pendingTool, tool: currentTool });
      }
    }
  }
}

/**
 * Sets signup details for fresh users who created their first board via AI Chat quick action.
 * Spawned as a detached task so board navigation is not delayed. 
 */
function* _updateSignupDetailsForAIBoardSaga({ accountId, boardId }) {
  if (!accountId) {
    return;
  }
  const state = yield select();
  if (isEmpty(signup.selectors.signupDetails(state))) {
    yield put(signup.actions.updateSignupDetails('FRESH_USER', { accountId, boardId }));
  }
}

/**
 * Processes a completed board creation by checking conditions and navigating to the new board.
 */
function* _processCompletedBoardCreation(state, chatId, pendingTool) {
  const { tool, statusKey, messageId, toolMessageId } = pendingTool;

  // Check if we're already on the target board - prevents infinite clone loop
  const targetBoardId = tool.output?.boardId;
  const currentBoardId = routerSelectors.pageBoardId(state);

  if (targetBoardId && targetBoardId === currentBoardId) {
    delete pendingBoardCreationTools[chatId];
    yield put(actions.markBoardCreationDone(chatId, toolMessageId));
    return;
  }

  // Clear pending data before dispatching
  delete pendingBoardCreationTools[chatId];

  yield spawn(_updateSignupDetailsForAIBoardSaga, { accountId: tool.output?.accountId, boardId: targetBoardId });
  yield spawn(_logAiBoardCreatedEventSaga, targetBoardId);

  yield call(_navigateToNewBoardSaga, tool, statusKey, chatId, messageId, toolMessageId);
}

/**
 * Extracts createNewBoard tool directly from action data.
 * This avoids expensive selector calls by working with raw action data.
 *
 * @param {Object} action - Firestore QUERY_SNAPSHOT action
 * @returns {Object|null} { chatId, tool, toolMessageId, messageId } or null if not found
 */
function* _extractCreateBoardToolFromAction(action) {
  if (!action.value) {
    return null;
  }

  const queries = Array.isArray(action.value) ? action.value : [action.value];

  for (const query of queries) {
    // Check if this is an ai-tool-messages query
    if (!query.id || !query.id.startsWith('ai-tool-messages-')) {
      continue;
    }

    const chatId = query.id.replace('ai-tool-messages-', '');

    // Get tool messages for the chat's last message only
    const state = yield select();
    const toolDocs = selectors.latestMessageTools(state, chatId);
    if (!toolDocs || toolDocs.length === 0) {
      continue;
    }

    // Collect ALL createNewBoard/cloneBoard tools from latest message's tool docs
    // A single doc can have multiple matching toolCalls (e.g. FAILED + SUCCESS retry)
    const allMatches = [];
    for (const doc of toolDocs) {
      const toolCalls = doc.toolCalls;

      if (!toolCalls || !Array.isArray(toolCalls)) {
        continue;
      }

      for (const tool of toolCalls) {
        if (tool.name === 'createNewBoard' || tool.name === 'cloneBoard') {
          allMatches.push({ chatId, tool, toolMessageId: doc.id, messageId: doc.messageId });
        }
      }
    }

    if (allMatches.length === 0) {
      continue;
    }

    // Filter out already-completed board creations (checked from persisted Redux state)
    const uncompleted = allMatches.filter(m => !selectors.isBoardCreationDone(state, m.chatId, m.toolMessageId));

    if (uncompleted.length === 0) {
      continue;
    }

    // Prefer SUCCESS, then latest invokedAt
    const successMatch = uncompleted.find(m => m.tool.status === 'SUCCESS');
    if (successMatch) {
      return successMatch;
    }

    let bestMatch = uncompleted[0];
    for (const match of uncompleted) {
      if ((match.tool.invokedAt || 0) >= (bestMatch.tool.invokedAt || 0)) {
        bestMatch = match;
      }
    }
    return bestMatch;
  }

  return null;
}

/**
 * Waits for the server-created cloned chat to appear in Firestore.
 *
 * Queries for an ai-chat document where forkedFrom.chatId and forkedFrom.messageId
 * match the source chat and message. Uses waitTillSucceed + once for a one-shot query
 * that resolves when the document appears.
 *
 * @param {String} sourceChatId - The original chat ID that was cloned
 * @param {String} messageId - The message ID associated with the board creation
 * @param {String} boardId - The newly created board ID to match context.boardId
 * @returns {String|null} The cloned chat ID, or null if not found
 */
function* _waitForClonedChatSaga(sourceChatId, messageId, boardId) {
  const queryId = `clone-wait-${sourceChatId}`;
  try {
    const state = yield select();
    const userId = auth.selectors.currentUserId(state);
    const query = firestoreRedux.query('ai-chats', {
      id: queryId,
      where: [
        ['userId', '==', userId],
        ['forkedFrom.chatId', '==', sourceChatId],
        ['forkedFrom.messageId', '==', messageId],
      ],
      orderBy: [['latestMessageSentAt', 'desc']],
      requesterId: queryId,
      waitTillSucceed: true,
      once: true
    });
    const docs = yield query.result;
    const matchedDoc = docs?.find(doc => doc.context?.boardId === boardId);
    return matchedDoc ? matchedDoc.id : null;
  } finally {
    firestoreRedux.cancelQueryByRequester(queryId);
  }
}

// Boards created more than this many ms ago are treated as pre-existing and skipped.
const BOARD_CREATION_RECENT_THRESHOLD_MS = 2 * 60 * 1000; // 2 minutes

/**
 * Returns true if the board was recently created by the current user.
 * Used to skip replays of already-completed board creations in fresh sessions (e.g. incognito).
 *
 * @param {String} boardId
 * @param {String} userId
 * @returns {Boolean}
 */
function* _isBoardRecentlyCreatedByCurrentUser(boardId, userId) {
  if (!boardId || !userId) return false;
  try {
    const query = firestoreRedux.getDocById('boards', boardId, { once: true, waitTillSucceed: true });
    const board = yield query.result;
    if (!board) return false;
    if (board.createdBy !== userId) return false;
    const createdAt = board.createdAt?.toMillis?.() ?? board.createdAt;
    return typeof createdAt === 'number' && Date.now() - createdAt < BOARD_CREATION_RECENT_THRESHOLD_MS;
  } catch (e) {
    console.error('_isBoardRecentlyCreatedByCurrentUser failed:', e);
    return false;
  }
}

/**
 * Logs the board created analytics event for boards created via AI helper.
 *
 * @param {String} boardId - The newly created board ID
 */
function* _logAiBoardCreatedEventSaga(boardId) {
  if (!boardId) return;
  yield call(analytics.logBoardCreatedEvent, boardId);
}

/**
 * Navigates to a newly created board after the server auto-clones the chat.
 *
 * Flow:
 * 1. Validates tool output (boardId, accountId)
 * 2. Races: waits for cloned chat vs 1-minute timeout
 * 3. If cloned chat found: transfers draft, closes AI chat, navigates with ?aichat=clonedChatId
 * 4. If timeout: closes AI chat, navigates without chat
 * 5. Disconnects old chat listeners, cleans up status tracking
 *
 * @param {Object} tool - createNewBoard/cloneBoard tool with output
 * @param {String} statusKey - Status tracking key for cleanup
 * @param {String} chatId - Source chat ID
 * @param {String} messageId - Message ID associated with the board creation tool
 * @param {String} toolMessageId - Tool message document ID
 */
function* _navigateToNewBoardSaga(tool, statusKey, chatId, messageId, toolMessageId) {
  if (!tool.output) {
    console.error('Board created but tool output is missing');
    return;
  }

  const { boardId, accountId } = tool.output;

  if (!boardId || !accountId) {
    console.error('Board created but missing required output fields:', tool.output);
    return;
  }

  let state = yield select();
  const shouldOpenCloneChat = feature.selectors.isAvailable(state, 'ai-chat-other-boards');

  // Wait for server-created cloned chat (race with timeout)
  let clonedChatId = null;
  if (shouldOpenCloneChat) {
    try {
      const result = yield race({
        clonedChatId: call(_waitForClonedChatSaga, chatId, messageId, boardId),
        timeout: delay(1 * 60 * 1000)
      });
      clonedChatId = result.clonedChatId || null;
    } catch (error) {
      console.error('Error waiting for cloned chat:', error);
    }

    // Transfer draft if cloned chat was found
    if (clonedChatId) {
      yield spawn(transferDraftToClonedChatSaga, chatId, clonedChatId);
    }

    // Refresh state after waiting (up to 1 minute may have passed)
    state = yield select();
  }
  const pageName = routerSelectors.pageName(state);
  const currentBoardId = routerSelectors.pageBoardId(state);
  const targetPath = `/${accountId}/board/${boardId}`;

  // Already on this board, just ensure chat is open with cloned chat ID if available
  if (pageName === 'BOARD' && currentBoardId === boardId) {
    if (clonedChatId) {
      const pageAiChat = routerSelectors.pageAiChat(state);
      if (pageAiChat.chatId !== clonedChatId) {
        yield call(router.setPageAiChatId, clonedChatId, true);
        yield spawn(disconnectFirestore, chatId);
      }
    }
    delete previousMessageStatus[statusKey];
    return;
  }

  // Close current AI chat panel before navigation (replace, not push).
  const isPageAiChatOpen = routerSelectors.isPageAiChatOpen(state);
  const isDialogAiChatOpen = routerSelectors.isDialogAiChatOpen(state);

  if (isPageAiChatOpen) {
    yield call(router.closePageAiChat, false);
  } else if (isDialogAiChatOpen) {
    yield call(router.closeDialogAiChat, false);
  }

  const navigatePath = clonedChatId ? `${targetPath}?aichat=${clonedChatId}` : targetPath;
  yield call(router.navigate, navigatePath);

  // Wait for the current microtask queue to drain (including disconnectedCallback of the old
  // ai-chat-panel which resets onceOpened to false). After this point, the new board's header
  // has dispatched `show-tip` and its 300ms debounce timer has started. Setting onceOpened=true
  // here ensures _processPendingTip sees it and suppresses the tip on auto-redirect.
  yield call(() => new Promise(resolve => setTimeout(resolve, 0)));
  yield put(knownFeatures.actions.changeOpenedOnce('AI_CHAT_NEW_CONVERSATION_TIP', true));
  yield put(knownFeatures.actions.changeOpenedOnce('AI_CHAT_HISTORY_TIP', true));

  // Cancel Firestore live listeners for the old chat after navigation
  yield spawn(disconnectFirestore, chatId);

  // Mark this board creation as done so it won't trigger again (persisted via Redux)
  yield put(actions.markBoardCreationDone(chatId, toolMessageId));

  // Clean up the tracked status
  delete previousMessageStatus[statusKey];
}

/**
 * Transfers draft message from source chat to target (cloned) chat.
 *
 * This saga is called from cloneChatSaga to preserve user's unsent draft.
 *
 * @param {String} sourceChatId - Original chat ID
 * @param {String} targetChatId - Cloned chat ID
 */
function* transferDraftToClonedChatSaga(sourceChatId, targetChatId) {
  if (!sourceChatId || !targetChatId) {
    return;
  }

  const state = yield select();
  const userId = auth.selectors.currentUserId(state);
  const oldDraft = selectors.draftMessage(state, sourceChatId);

  // Transfer draft to new cloned chat if exists
  if (oldDraft && oldDraft.trim()) {
    yield put({
      type: actions.SET_DRAFT_MESSAGE,
      chatId: targetChatId,
      draftText: oldDraft,
      userId
    });
    // Clear draft from old chat
    yield put(actions.clearDraftMessage(sourceChatId, userId));
  }
}

/**
 * Shows chat history view by updating URL.
 *
 * Flow:
 * 1. Gets current context to determine which URL param to update
 * 2. Adds ?show-chat-history=true while preserving the current ?aichat or ?card-aichat value
 */
function* showChatHistorySaga() {
  const state = yield select();
  const currentContext = selectors.currentContext(state);

  // Show chat history based on context type
  // CARD context uses replace (push=false) to avoid extra history entries
  const isCard = currentContext?.type === 'CARD';
  const showHistory = isCard ? router.showDialogAiChatHistory : router.showPageAiChatHistory;
  yield call(showHistory, !isCard);
}

/**
 * Hides the chat history view, returning to the current chat.
 *
 * Non-CARD contexts push a history entry when opening history, so a browser
 * back() pops it. CARD context opens history via replace (no pushed entry),
 * so back() would exit the card dialog entirely — instead we remove the
 * `show-chat-history` param via replace, keeping the card dialog open.
 */
function* hideChatHistorySaga() {
  const state = yield select();
  const currentContext = selectors.currentContext(state);
  const isCard = currentContext?.type === 'CARD';

  if (isCard) {
    yield call(router.hideDialogAiChatHistory, false); // replace, not push
    return;
  }

  yield call(router.back);
}

/**
 * Opens an existing chat by updating URL with the chat ID.
 *
 * Flow:
 * 1. Gets current context to determine which URL param to update
 * 2. Sets URL to ?aichat=chatId or ?card-aichat=chatId, removing ?show-chat-history in the same step
 * 3. Chat data loading is handled by watchRouterSaga when URL changes
 */
function* openExistingChatSaga(action) {
  const { chatId } = action;
  if (!chatId) {
    console.warn('Cannot open existing chat: chatId is required');
    return;
  }

  const state = yield select();
  const currentContext = selectors.currentContext(state);

  const isCard = currentContext?.type === 'CARD';
  const openAiChat = isCard ? router.openDialogAiChat : router.openPageAiChat;
  yield call(openAiChat, chatId, !isCard); // CARD replaces, BOARD pushes
}

/**
 * Navigates to the source board of an incoming cross-board proposal and opens its chat there —
 * dispatched by ai-chat-fab-button's [View AI Chat]. The page AI chat panel is URL-driven (the
 * `aichat` query param), so a single push to `/${accountId}/board/${sourceBoardId}?aichat=${chatId}`
 * both navigates and opens the chat. It's a push (not replace), so browser Back returns to the
 * board the user came from (where the pending edits — and the [View AI Chat] bar — still show).
 */
function* viewAiChatSaga(action) {
  const { chatId, sourceBoardId, cardId } = action;
  if (!chatId || !sourceBoardId) {
    console.warn('viewAiChatSaga: chatId and sourceBoardId are required');
    return;
  }
  const state = yield select();
  // The source board may belong to a different account, so use its own account for
  // the URL (not the current one). Fall back to the current account, and skip if
  // neither is found so we don't navigate to `/undefined/board/...`.
  const accountId = boardSelectors.accountIdOfBoard(state, sourceBoardId) || routerSelectors.accountId(state);
  if (!accountId) {
    console.warn('viewAiChatSaga: could not resolve account for source board', sourceBoardId);
    return;
  }

  // Navigate to the target, then open the RIGHT panel with the router's own action — a card's
  // chat opens the card and its dialog panel (`openDialogAiChat` → `card-aichat`), a board chat
  // opens the board's page panel (`openPageAiChat` → `aichat`). A hand-built URL left the board's
  // own page panel showing behind the card.
  if (cardId) {
    yield call(router.navigate, `/${accountId}/board/${sourceBoardId}/${cardId}`);
    yield call(router.openDialogAiChat, chatId, false);
  } else {
    yield call(router.navigate, `/${accountId}/board/${sourceBoardId}`);
    yield call(router.openPageAiChat, chatId, false);
  }
}

/**
 * Adds or updates a reaction on a response.
 *
 * Calls PUT /ai/chats/{chatId}/messages/{messageId}/responses/{responseId}/reaction
 * with { reactionType: 'LIKE' | 'DISLIKE' }.
 * Server returns 204 No Content. Firestore syncs the updated reaction automatically.
 *
 * @param {Object} action - Redux action with chatId, messageId, responseId, reactionType
 */
function* addReactionSaga(action) {
  const { chatId, messageId, responseId, reactionType } = action;
  const messagesQueryId = `ai-messages-${chatId}`;
  const state = yield select();
  const currentMessage = selectors.message(state, chatId, messageId);

  // Local write for instant UI feedback
  if (currentMessage && currentMessage.responses) {
    const updatedResponses = currentMessage.responses.map(r =>
      r.id === responseId ? { ...r, reaction: { type: reactionType } } : r
    );
    firestoreRedux.save('ai-messages', { ...currentMessage, responses: updatedResponses }, { localWrite: true, queryId: messagesQueryId });
  }

  try {
    yield call(requestApi, `/ai/chats/${chatId}/messages/${messageId}/responses/${responseId}/reaction`, {
      method: 'PUT',
      body: { reactionType }
    });
  } catch (error) {
    console.error('Failed to add reaction:', error);
    // Rollback local write
    if (currentMessage) {
      firestoreRedux.save('ai-messages', currentMessage, { localWrite: true, queryId: messagesQueryId });
    }
    showSnackBar({
      type: 'ERROR',
      message: i18next.t('ai-chat:toast.failedToReact')
    });
  }
}

/**
 * Removes a reaction from a response.
 *
 * Calls PUT /ai/chats/{chatId}/messages/{messageId}/responses/{responseId}/reaction
 * with empty body {}. Server returns 204 No Content. Firestore syncs automatically.
 *
 * @param {Object} action - Redux action with chatId, messageId, responseId
 */
function* removeReactionSaga(action) {
  const { chatId, messageId, responseId } = action;
  const messagesQueryId = `ai-messages-${chatId}`;
  const state = yield select();
  const currentMessage = selectors.message(state, chatId, messageId);

  // Local write for instant UI feedback
  if (currentMessage && currentMessage.responses) {
    const updatedResponses = currentMessage.responses.map(r =>
      r.id === responseId ? { ...r, reaction: null } : r
    );
    firestoreRedux.save('ai-messages', { ...currentMessage, responses: updatedResponses }, { localWrite: true, queryId: messagesQueryId });
  }

  try {
    yield call(requestApi, `/ai/chats/${chatId}/messages/${messageId}/responses/${responseId}/reaction`, {
      method: 'PUT',
      body: {}
    });
  } catch (error) {
    console.error('Failed to remove reaction:', error);
    // Rollback local write
    if (currentMessage) {
      firestoreRedux.save('ai-messages', currentMessage, { localWrite: true, queryId: messagesQueryId });
    }
    showSnackBar({
      type: 'ERROR',
      message: i18next.t('ai-chat:toast.failedToReact')
    });
  }
}

/**
 * Copies message text to system clipboard and shows toast notification.
 *
 * For REQUEST messages: Extracts `request.userPrompt` from the message.
 * For RESPONSE messages: Locates the specific response by `responseId` in the `responses[]` array
 * and extracts `content` (preserves Markdown formatting).
 *
 * @param {Object} action - Redux action with messageId, messageType, and optional responseId
 */
function* copyMessageSaga(action) {
  const { messageId, messageType, responseId } = action;

  try {
    const state = yield select();
    const chatId = selectors.currentChatId(state);

    if (!chatId) {
      showSnackBar({ message: i18next.t('ai-chat:toast.failedToCopy'), type: 'ERROR' });
      return;
    }

    const message = firestoreRedux.selectors.doc(state, 'ai-messages', messageId);

    if (!message) {
      showSnackBar({ message: i18next.t('ai-chat:toast.failedToCopy'), type: 'ERROR' });
      return;
    }

    let textToCopy;

    if (messageType === 'REQUEST') {
      if (message.request?.userPrompt) {
        const htmlContent = promptToEditorValue(message.request.userPrompt);
        const plainText = toPlainText(htmlContent);
        const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
        const textBlob = new Blob([plainText], { type: 'text/plain' });
        yield call([navigator.clipboard, navigator.clipboard.write], [new ClipboardItem({ 'text/html': htmlBlob, 'text/plain': textBlob })]);
        showSnackBar({ message: i18next.t('ai-chat:toast.messageCopied') });
        return;
      }
    } else if (messageType === 'RESPONSE') {
      const response = message.responses?.find(response => response.id === responseId);
      if (!response) {
        showSnackBar({ message: i18next.t('ai-chat:toast.failedToCopy'), type: 'ERROR' });
        return;
      }
      const markdownContent = selectors.responseContent(state, chatId, messageId, responseId);
      if (markdownContent) {
        // Write as both text/html (rendered markdown) and text/plain (raw markdown).
        const htmlContent = marked(markdownContent, { breaks: true });
        const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
        const textBlob = new Blob([markdownContent], { type: 'text/plain' });
        yield call([navigator.clipboard, navigator.clipboard.write], [new ClipboardItem({ 'text/html': htmlBlob, 'text/plain': textBlob })]);
        showSnackBar({ message: i18next.t('ai-chat:toast.messageCopied') });
        return;
      }
    }

    if (!textToCopy) {
      showSnackBar({ message: i18next.t('ai-chat:toast.failedToCopy'), type: 'ERROR' });
      return;
    }

    clipboard.copy(textToCopy);
    showSnackBar({ message: i18next.t('ai-chat:toast.messageCopied') });
  } catch (error) {
    console.error('Failed to copy message:', error);
    showSnackBar({ message: i18next.t('ai-chat:toast.failedToCopy'), type: 'ERROR' });
  }
}

/**
 * Regenerates AI response for a message.
 *
 * Calls API POST /ai/chats/{chatId}/messages/{messageId}/regenerate.
 * Uses localWrite pattern for instant UI feedback before server responds.
 *
 * @param {Object} action - Redux action with chatId and messageId
 */
function* regenerateResponseSaga(action) {
  const { chatId, messageId } = action;

  if (!chatId || !messageId) {
    console.warn('Cannot regenerate response: chatId and messageId are required');
    return;
  }

  const messagesQueryId = `ai-messages-${chatId}`;
  const state = yield select();
  const currentMessage = selectors.message(state, chatId, messageId);

  try {
    const now = Date.now();
    const newResponse = {
      id: `aicmr_${entityIdProvider.getId()}`,
      status: 'GENERATING',
      createdAt: now,
      statusAt: now,
    };

    // Local write to show instant UI feedback
    if (currentMessage) {
      firestoreRedux.save('ai-messages', {
        ...currentMessage,
        status: 'GENERATING',
        statusAt: now,
        responses: [...(currentMessage.responses || []), newResponse],
      }, { localWrite: true, queryId: messagesQueryId });
    }

    yield call(requestApi, `/ai/chats/${chatId}/messages/${messageId}/regenerate`, {
      method: 'POST',
      body: { responseId: newResponse.id }
    });
  } catch (error) {
    console.error('Failed to regenerate response:', error);
    // Rollback local write on error
    if (currentMessage) {
      firestoreRedux.save('ai-messages', currentMessage, { localWrite: true, queryId: messagesQueryId });
    }
    showSnackBar({
      type: 'ERROR',
      message: i18next.t('ai-chat:toast.failedToRegenerate')
    });
  }
}

/**
 * Shares a chat conversation by calling the server API.
 *
 * Steps:
 * 1. Calls POST /ai/chats/{chatId}/share with { shared: true }
 * 2. On success: Constructs the shared chat link URL (with optional messageId for deep linking),
 *    copies it to clipboard, and shows success notification.
 * 3. On error: Shows error notification.
 *
 * @param {Object} action - Redux action with chatId, messageId (optional), shared
 */
/**
 * Renames a chat.
 *
 * Writes the new title onto the chat document first so the row changes under the user's hand, then
 * calls the server. A failure puts the previous title back; a network failure does so silently,
 * since the user already knows they are offline.
 */
function* renameSaga({ chatId, title }) {
  const state = yield select();
  const currentDoc = firestoreRedux.selectors.doc(state, 'ai-chats', chatId);
  if (!currentDoc || !title) {
    return;
  }

  try {
    firestoreRedux.save('ai-chats', { ...currentDoc, title }, { localWrite: true });
    yield call(requestApi, `/ai/chats/${chatId}/title`, {
      method: 'POST',
      body: { title },
      excludeErrors: [400, 404]
    });
  } catch (error) {
    firestoreRedux.save('ai-chats', currentDoc, { localWrite: true });
    if (isNetworkError(error)) {
      return;
    }
    console.error('Failed to rename chat:', error);
    showSnackBar({ type: 'ERROR', message: i18next.t('ai-chat:toast.failedToRenameChat') });
  }
}

/**
 * Deletes a chat.
 *
 * The queryId is the history list's own query, not the chat's: the reducer only drops the id from a
 * query result when it is told which query, and that list is where the row has to disappear from.
 *
 * A delete of the chat the panel is on leaves a new chat open, which the history list showing over
 * that chat makes reachable — closing the list would otherwise return to a chat that is gone.
 */
function* delSaga({ chatId }) {
  const state = yield select();
  const currentDoc = firestoreRedux.selectors.doc(state, 'ai-chats', chatId);
  if (!currentDoc) {
    return;
  }

  const context = selectors.currentContext(state);
  const queryId = selectors.recentChatQueryId(context?.type, context?.boardId, context?.cardId);

  try {
    firestoreRedux.delete('ai-chats', chatId, { localWrite: true, queryId });
    if (selectors.currentChatId(state) === chatId) {
      yield put(actions.startNewChatSession());
    }
    yield call(requestApi, `/ai/chats/${chatId}`, { method: 'DELETE', excludeErrors: [404, 409] });
  } catch (error) {
    firestoreRedux.save('ai-chats', currentDoc, { localWrite: true, queryId });
    if (isNetworkError(error)) {
      return;
    }
    console.error('Failed to delete chat:', error);
    showSnackBar({ type: 'ERROR', message: i18next.t('ai-chat:toast.failedToDeleteChat') });
  }
}

function* shareChatSaga(action) {
  const { chatId, messageId, shared } = action;

  if (!chatId) {
    console.warn('shareChatSaga: chatId is required');
    return;
  }

  try {
    yield call(requestApi, `/ai/chats/${chatId}/share`, {
      method: 'POST',
      body: { shared }
    });

    // Construct shared chat URL.
    // Base: origin + pathname (no existing query params carried over).
    // Param name must match the chat's context: CARD chats live in the card dialog
    // (`card-aichat`), everything else is page-level (`aichat`).
    // &messageId= is appended when a specific message should be deep-linked.
    const state = yield select();
    const isCardContext = selectors.currentContext(state)?.type === 'CARD';
    const aiChatParam = isCardContext ? 'card-aichat' : 'aichat';
    const baseUrl = window.location.origin + window.location.pathname;
    const chatUrl = messageId
      ? `${baseUrl}?${aiChatParam}=${chatId}&messageId=${messageId}`
      : `${baseUrl}?${aiChatParam}=${chatId}`;

    clipboard.copy(chatUrl);
    showSnackBar({ message: i18next.t('ai-chat:toast.chatShared') });
  } catch (error) {
    console.error('Failed to share chat:', error);
    showSnackBar({
      type: 'ERROR',
      message: i18next.t('ai-chat:toast.failedToShareChat')
    });
  }
}

/**
 * Predicate for debounce: only triggers on QUERY_SNAPSHOT actions
 * that contain ai-tool-messages or ai-messages queries.
 * This prevents unrelated Firestore snapshots from resetting the debounce timer.
 */
/**
 * Snapshot predicate for a chat's tool messages — the moment new ToolCalls, and so new
 * card references, can appear.
 */
function _isToolMessagesSnapshot(action) {
  if (action.type !== firestoreRedux.actions.QUERY_SNAPSHOT || !action.value) {
    return false;
  }
  const queries = Array.isArray(action.value) ? action.value : [action.value];
  return queries.some((query) => query.id && query.id.startsWith('ai-tool-messages-'));
}

/**
 * Loads what the loaded chats' ToolCalls point at — the target cards, and the columns of
 * the boards they live on — so the AI Helper can name a card and its column even for
 * cards on boards the user hasn't opened.
 *
 * The board page loads the cards too (board/saga.js `loadReferencedCards`), but only for
 * chats that still have edits pending on it. Once a cross-board proposal is applied the
 * chat drops out of that query, so on the next reload nothing fetches those cards and
 * their rows render title-less.
 *
 * `createCard` targets are skipped — they're materialised via localWrite and don't exist
 * on the server — as is anything already in state.
 */
function* loadCardsFromToolCallsSaga() {
  const state = yield select();
  const toolMessages = firestoreRedux.selectors.allDocs(state, 'ai-tool-messages') || [];
  const referencedCards = new Map(); // cardId -> { boardId, chatId }
  const referencedBoards = new Map(); // boardId -> chatId
  for (const toolMessage of toolMessages) {
    for (const toolCall of toolMessage.toolCalls || []) {
      if (toolCall.name === 'createCard') continue;
      if (toolCall.input?.cardId && toolCall.input?.boardId) {
        referencedCards.set(toolCall.input.cardId, { boardId: toolCall.input.boardId, chatId: toolMessage.chatId });
        referencedBoards.set(toolCall.input.boardId, toolMessage.chatId);
      }
    }
  }

  for (const [cardId, { boardId, chatId }] of referencedCards) {
    if (firestoreRedux.selectors.doc(state, 'cards', cardId)) continue;
    // Cards are read from `boards/{boardId}/cards`, but land in `firestore.docs.cards` —
    // which is where the panel looks for them.
    firestoreRedux.getDocById(`boards/${boardId}/cards`, cardId, { requesterId: `ai-tool-cards-${chatId}` });
  }

  // The column name beside each card comes from its board's columns, which only load when
  // that board is open. Same query id as board/saga.js keeps it to one listener — opening
  // that board later just takes it over.
  for (const [boardId, chatId] of referencedBoards) {
    if (!isEmpty(boardSelectors.columns(state, boardId))) continue;
    firestoreRedux.query('columns', {
      id: `board_columns_${boardId}`,
      where: [['boardId', '==', boardId]],
      requesterId: `ai-tool-cards-${chatId}`,
    });
  }
}

function _isAiChatSnapshot(action) {
  if (action.type !== firestoreRedux.actions.QUERY_SNAPSHOT || !action.value) {
    return false;
  }
  const queries = Array.isArray(action.value) ? action.value : [action.value];
  return queries.some(q => q.id && (q.id.startsWith('ai-tool-messages-') || q.id.startsWith('ai-messages-')));
}

/**
 * Approves one or more tool calls for a response. localWrites each target tool
 * to `IN_PROGRESS` for instant UI feedback, then POSTs to the approve endpoint.
 * Authoritative status arrives via the Firestore subscription.
 */
function* approveToolCallsSaga(action) {
  const { responseId, toolCallIds } = action;
  if (!responseId || !toolCallIds?.length) return;

  const state = yield select();
  // Find the tool-message doc that owns this responseId — derive chatId from
  // there rather than from `currentChatId`. The user can resolve an AI
  // proposal from the board card tooltip with the AI Helper panel closed
  // (panel-closed → `currentChatId` is null), so any panel-state dependency
  // here would silently drop both the localWrite and the API POST.
  const allDocs = firestoreRedux.selectors.allDocs(state, 'ai-tool-messages') || [];
  const idSet = new Set(toolCallIds);
  const matched = allDocs.filter(doc =>
    doc.responseId === responseId && doc.toolCalls?.some(tc => idSet.has(tc.id))
  );
  if (matched.length === 0) return;

  const chatId = matched[0].chatId;
  const messageId = matched[0].messageId;
  const queryId = `ai-tool-messages-${chatId}`;
  for (const doc of matched) {
    firestoreRedux.save('ai-tool-messages', {
      ...doc,
      toolCalls: doc.toolCalls.map(tc => idSet.has(tc.id)
        ? { ...tc, status: 'IN_PROGRESS', statusAt: Date.now() }
        : tc
      ),
    }, { localWrite: true, queryId });
  }

  // A proposal written from what the user had unsent replaces it, so approving it drops that draft.
  // The drafts go here rather than in each view, which would have to watch for the change landing
  // and would show the old text again in the meantime.
  // The page draft rides along with every turn, so only a chat the user started from a quick action
  // (Polish) was asked to rewrite it; anywhere else the draft stays for the user to send.
  const isQuickActionChat = (firestoreRedux.selectors.allDocs(state, 'ai-messages') || [])
    .some(message => message.chatId === chatId && message.request?.quickAction);
  const requestDraft = isQuickActionChat
    ? get(firestoreRedux.selectors.doc(state, 'ai-messages', messageId), 'request.context.draft')
    : undefined;
  const draftProposals = matched.flatMap(doc => (doc.toolCalls || []).filter(tc => idSet.has(tc.id) && _proposalDraftAction(tc, '')));
  const clearedDrafts = new Map();
  for (const tc of draftProposals) {
    if (!_isWrittenFromDraft(tc, requestDraft)) continue;
    clearedDrafts.set(tc.id, _proposalDraft(state, tc));
    yield put(_proposalDraftAction(tc, ''));
  }

  try {
    yield call(requestApi, `/ai/chats/${chatId}/messages/${messageId}/responses/${responseId}/tool-calls/accept`, {
      method: 'POST',
      body: { toolCallIds },
    });
  } catch (error) {
    console.error('approveToolCallsSaga failed:', error);
    // Apply never reached the server: the proposal comes back to be decided, with the user's text.
    // The views dropped their draft on Apply, so a correction is only on the proposal by now.
    const restoredIds = new Set(draftProposals.map(tc => tc.id));
    for (const doc of matched) {
      firestoreRedux.save('ai-tool-messages', {
        ...doc,
        toolCalls: doc.toolCalls.map(tc => idSet.has(tc.id) && !restoredIds.has(tc.id)
          ? { ...tc, status: 'IN_PROGRESS', statusAt: Date.now() }
          : tc
        ),
      }, { localWrite: true, queryId });
    }
    for (const tc of draftProposals) {
      const editedText = tc.editedInput && (tc.editedInput.text || tc.editedInput.description);
      if (editedText) {
        yield call(_restoreProposalCorrection, tc, _markdownToEditorValue(state, editedText));
      } else if (clearedDrafts.get(tc.id)) {
        yield call(_restoreProposalCorrection, tc, clearedDrafts.get(tc.id), cardDetailsSelectors.descriptionDraftOriginal(state, tc.input?.cardId));
      } else {
        yield put(actions._toolCallInputFailed(tc.id));
      }
    }
  }
}

/**
 * Whether the request that produced `toolCall` sent the draft it would replace, read from the
 * request's `context.draft`: a new chat message from the chat box, an edit from that message's open
 * edit, a description from the description draft.
 */
const _isWrittenFromDraft = (toolCall, requestDraft) => {
  if (!requestDraft) return false;
  const chatMessage = requestDraft.chatMessage;
  if (selectors.isCreateChatMessageTool(toolCall.name)) return !!chatMessage && !chatMessage.chatMessageId;
  if (selectors.isUpdateChatMessageTool(toolCall.name)) return !!chatMessage?.chatMessageId && chatMessage.chatMessageId === toolCall.input?.chatMessageId;
  return toolCall.name === 'updateCardDescription' && !!requestDraft.description;
};

/** A proposal's Markdown as the field editor's HTML, as `proposalToEditorValue` gives the views. */
const _markdownToEditorValue = (state, markdown) =>
  toEditorValue({ description: markdownToHtml(markdown) }, { titleOf: titleOf(state), isOurUrl: router.isAppUrl });

/**
 * The action that sets the unsent draft a chat or description proposal was written over; an empty
 * `text` drops it.
 * @returns {Object|null} The action, or null for any other tool.
 */
const _proposalDraftAction = (toolCall, text, originalDescription) => {
  const input = toolCall.input || {};
  if (selectors.isCreateChatMessageTool(toolCall.name)) {
    const containerId = input.cardId || input.boardId;
    return containerId ? chatActions.updateNewChatDraft(containerId, text) : null;
  }
  if (selectors.isUpdateChatMessageTool(toolCall.name)) {
    return input.chatMessageId ? chatActions.updateEditDraft(input.chatMessageId, text) : null;
  }
  if (toolCall.name === 'updateCardDescription' && input.cardId) {
    return cardDetailsActions.updateDescriptionDraft(input.cardId, text || undefined, originalDescription);
  }
  return null;
};

/** The unsent text a chat or description proposal was written over, as it stands in `state`. */
const _proposalDraft = (state, toolCall) => {
  const input = toolCall.input || {};
  if (selectors.isCreateChatMessageTool(toolCall.name)) return chatSelectors.newChatDraft(state, input.cardId || input.boardId);
  if (selectors.isUpdateChatMessageTool(toolCall.name)) return chatSelectors.editDraft(state, input.chatMessageId);
  if (toolCall.name === 'updateCardDescription') return cardDetailsSelectors.descriptionDraft(state, input.cardId);
  return '';
};

/**
 * Saves the user's version of a still-waiting proposal as `editedInput`; with `shouldApprove`, then approves it.
 *
 * The chat and message ids come from the tool-message doc that owns the response, for the same
 * reason the approve saga takes them from there: a proposal can be decided with the AI Helper panel
 * closed, so anything read from panel state would silently drop the call.
 */
function* updateToolCallInputSaga(action) {
  const { responseId, toolCallId, shouldApprove, htmlField } = action;
  if (!responseId || !toolCallId || !action.input) return;
  // The user's wording, kept as the editor's HTML to give back as their draft if this never lands.
  const editorHtml = action.input.text || action.input.description;

  const state = yield select();
  const allDocs = firestoreRedux.selectors.allDocs(state, 'ai-tool-messages') || [];
  const owner = allDocs.find(doc =>
    doc.responseId === responseId && doc.toolCalls?.some(tc => tc.id === toolCallId)
  );
  if (!owner) {
    if (shouldApprove) yield put(actions._toolCallInputFailed(toolCallId));
    return;
  }

  // Only a proposal still waiting can be corrected: a typing save can land after the user decided, and the server answers 409.
  const toolCall = owner.toolCalls.find(tc => tc.id === toolCallId);
  if (toolCall?.status !== 'WAITING_FOR_APPROVAL') {
    // Decided elsewhere meanwhile: the APPROVED overlay this Apply set would hide it with nothing approved.
    if (shouldApprove) yield call(_restoreProposalCorrection, toolCall, editorHtml);
    return;
  }

  // The AI writes Markdown, so the correction is saved as Markdown too.
  const input = htmlField && action.input[htmlField]
    ? { ...action.input, [htmlField]: yield call(promptToMarkdown, action.input[htmlField]) }
    : action.input;

  // Put the correction on the doc before the request, so everything reading the proposal sees the
  // user's own words right away — the thread shows a sent message from this, and the server's
  // round-trip is long enough for the proposal's original text to show instead.
  firestoreRedux.save('ai-tool-messages', {
    ...owner,
    toolCalls: owner.toolCalls.map(tc => (tc.id === toolCallId ? { ...tc, editedInput: input } : tc)),
  }, { localWrite: true, queryId: `ai-tool-messages-${owner.chatId}` });

  try {
    yield call(
      requestApi,
      `/ai/chats/${owner.chatId}/messages/${owner.messageId}/responses/${responseId}/tool-calls/${toolCallId}/input`,
      { method: 'PUT', body: { input } },
    );
  } catch (error) {
    console.error('updateToolCallInputSaga failed:', error);
    // The server never saw the wording, so nothing is approved and the proposal comes back to be decided.
    if (shouldApprove) yield call(_restoreProposalCorrection, toolCall, editorHtml);
    return;
  }

  if (shouldApprove) {
    yield put(actions.approveToolCalls(responseId, [toolCallId]));
  }
}

/**
 * Undoes the Apply of a correction that was never approved, and gives the user's wording (editor
 * HTML) back as their draft: the editor closed on Apply, so it would be lost otherwise.
 */
function* _restoreProposalCorrection(toolCall, html, originalDescription) {
  yield put(actions._toolCallInputFailed(toolCall.id));
  const state = yield select();
  const restoreDraft = _proposalDraftAction(toolCall, html, originalDescription ?? cardDetailsSelectors.editorDescription(state, toolCall.input?.cardId));
  if (restoreDraft) yield put(restoreDraft);
}

/**
 * Runs the corrections of one proposal in order, so the last one the user made is the one the server
 * holds. A save still queued behind another is dropped for a newer one; a save already sending is
 * waited for, as its PUT cannot be called back once sent. An approve is never dropped.
 */
function* watchToolCallInputUpdates() {
  const latestByToolCallId = {};
  while (true) {
    const action = yield take(actions.UPDATE_TOOL_CALL_INPUT);
    const previous = latestByToolCallId[action.toolCallId];
    const update = { action, previous, isStarted: false };
    if (previous && !previous.isStarted && !previous.action.shouldApprove) {
      yield cancel(previous.task);
      update.previous = previous.previous;
    }
    update.task = yield fork(_runToolCallInputUpdate, update);
    latestByToolCallId[action.toolCallId] = update;
  }
}

function* _runToolCallInputUpdate(update) {
  if (update.previous) yield join(update.previous.task);
  update.isStarted = true;
  update.previous = null;
  yield call(updateToolCallInputSaga, update.action);
}

/**
 * Approves every WAITING_FOR_APPROVAL leaf edit ToolCall on a card.
 *
 * Resolves leaves across all responses in the chat (a card's leaves may be
 * spread over multiple responses if regenerated), groups them by `responseId`,
 * and dispatches `approveToolCalls` once per response.
 */
function* approveCardToolCallsSaga(action) {
  const { chatId, cardId } = action;
  if (!chatId || !cardId) {
    console.warn('approveCardToolCallsSaga: chatId and cardId required');
    return;
  }

  const state = yield select();
  const toolMessageDocs = selectors.allToolMessages(state, chatId) || [];
  // responseId -> [toolCallIds]
  // Match every WAITING tool call targeting this card — createCard /
  // deleteCard / updateCard* / moveCard / sub-entity mutations. The earlier
  // `leafTools`-only filter made the card-level Approve a no-op for NEW
  // (createCard) and DELETE (deleteCard) since those aren't leaves of the
  // editCard group.
  const byResponse = new Map();
  for (const doc of toolMessageDocs) {
    for (const tc of doc.toolCalls || []) {
      if (
        tc.status === 'WAITING_FOR_APPROVAL' &&
        tc.input?.cardId === cardId
      ) {
        if (!byResponse.has(doc.responseId)) byResponse.set(doc.responseId, []);
        byResponse.get(doc.responseId).push(tc.id);
      }
    }
  }

  if (byResponse.size === 0) return;

  for (const [responseId, toolCallIds] of byResponse) {
    yield put(actions.approveToolCalls(responseId, toolCallIds));
  }
}

/**
 * Rejects one or more tool calls for a response. localWrites each target tool
 * to `REJECTED` for instant UI feedback, then POSTs to the reject endpoint.
 * Authoritative status arrives via the Firestore subscription.
 */
function* rejectToolCallsSaga(action) {
  const { responseId, toolCallIds } = action;
  if (!responseId || !toolCallIds?.length) return;

  const state = yield select();
  // Derive chatId from the matched tool-message doc — see comment in
  // approveToolCallsSaga for why we don't read `currentChatId` here.
  const allDocs = firestoreRedux.selectors.allDocs(state, 'ai-tool-messages') || [];
  const idSet = new Set(toolCallIds);
  const matched = allDocs.filter(doc =>
    doc.responseId === responseId && doc.toolCalls?.some(tc => idSet.has(tc.id))
  );
  if (matched.length === 0) return;

  const chatId = matched[0].chatId;
  const messageId = matched[0].messageId;
  const queryId = `ai-tool-messages-${chatId}`;
  for (const doc of matched) {
    firestoreRedux.save('ai-tool-messages', {
      ...doc,
      toolCalls: doc.toolCalls.map(tc => idSet.has(tc.id)
        ? { ...tc, status: 'REJECTED', statusAt: Date.now() }
        : tc
      ),
    }, { localWrite: true, queryId });
  }

  try {
    yield call(requestApi, `/ai/chats/${chatId}/messages/${messageId}/responses/${responseId}/tool-calls/reject`, {
      method: 'POST',
      body: { toolCallIds },
    });
  } catch (error) {
    console.error('rejectToolCallsSaga failed:', error);
  }
}

/**
 * Rejects every WAITING_FOR_APPROVAL leaf edit ToolCall on a card.
 * Mirror of `approveCardToolCallsSaga`.
 */
function* rejectCardToolCallsSaga(action) {
  const { chatId, cardId } = action;
  if (!chatId || !cardId) {
    console.warn('rejectCardToolCallsSaga: chatId and cardId required');
    return;
  }

  const state = yield select();
  const toolMessageDocs = selectors.allToolMessages(state, chatId) || [];
  // Match every WAITING tool call targeting this card — same scope as the
  // approve counterpart (createCard / deleteCard / updateCard* / moveCard
  // / sub-entity mutations).
  const byResponse = new Map();
  for (const doc of toolMessageDocs) {
    for (const tc of doc.toolCalls || []) {
      if (
        tc.status === 'WAITING_FOR_APPROVAL' &&
        tc.input?.cardId === cardId
      ) {
        if (!byResponse.has(doc.responseId)) byResponse.set(doc.responseId, []);
        byResponse.get(doc.responseId).push(tc.id);
      }
    }
  }

  if (byResponse.size === 0) return;

  for (const [responseId, toolCallIds] of byResponse) {
    yield put(actions.rejectToolCalls(responseId, toolCallIds));
  }
}

/**
 * Reverses a previous rejection on one or more tool calls. localWrites each
 * target tool back to `WAITING_FOR_APPROVAL` for instant UI feedback, then
 * POSTs to the undo-reject endpoint. Authoritative status arrives via the
 * Firestore subscription.
 */
function* undoRejectToolCallsSaga(action) {
  const { responseId, toolCallIds } = action;
  if (!responseId || !toolCallIds?.length) return;

  const state = yield select();
  // Derive chatId from the matched tool-message doc — see approveToolCallsSaga.
  const allDocs = firestoreRedux.selectors.allDocs(state, 'ai-tool-messages') || [];
  const idSet = new Set(toolCallIds);
  const matched = allDocs.filter(doc =>
    doc.responseId === responseId && doc.toolCalls?.some(tc => idSet.has(tc.id))
  );
  if (matched.length === 0) return;

  const chatId = matched[0].chatId;
  const messageId = matched[0].messageId;
  const queryId = `ai-tool-messages-${chatId}`;
  for (const doc of matched) {
    firestoreRedux.save('ai-tool-messages', {
      ...doc,
      toolCalls: doc.toolCalls.map(tc => idSet.has(tc.id)
        ? { ...tc, status: 'WAITING_FOR_APPROVAL', statusAt: Date.now() }
        : tc
      ),
    }, { localWrite: true, queryId });
  }

  try {
    yield call(requestApi, `/ai/chats/${chatId}/messages/${messageId}/responses/${responseId}/tool-calls/undo-reject`, {
      method: 'POST',
      body: { toolCallIds },
    });
  } catch (error) {
    console.error('undoRejectToolCallsSaga failed:', error);
  }
}


function* saga() {
  // Sync initial state on startup (handles page refresh)
  yield call(watchRouterSaga);
  yield all([
    takeEvery(actions.OPEN_AI_CHAT, openAiChatSaga),
    takeEvery(actions.CLOSE_AI_CHAT, closeAiChatSaga),
    takeEvery(actions.START_NEW_CHAT, startNewChatSaga),
    takeEvery(actions.START_NEW_CHAT_SESSION, startNewChatSessionSaga),
    takeEvery(actions.START_NEW_CHAT_USING_QUICK_ACTION, startNewChatUsingQuickActionSaga),
    takeEvery(actions.START_NEW_CHAT_USING_QUICK_ACTION_BY_NAME, startNewChatUsingQuickActionByNameSaga),
    takeEvery(actions.SHOW_CHAT_HISTORY, showChatHistorySaga),
    takeEvery(actions.HIDE_CHAT_HISTORY, hideChatHistorySaga),
    takeEvery(actions.OPEN_EXISTING_CHAT, openExistingChatSaga),
    takeEvery(actions.VIEW_AI_CHAT, viewAiChatSaga),
    takeEvery(actions.EDIT_MESSAGE, editMessageSaga),
    takeEvery(actions.SEND_MESSAGE, sendMessageSaga),
    takeLatest(actions.CANCEL_MESSAGE, cancelMessageSaga),
    takeEvery(actions.REGENERATE_RESPONSE, regenerateResponseSaga),
    takeEvery(actions.ADD_REACTION, addReactionSaga),
    takeEvery(actions.REMOVE_REACTION, removeReactionSaga),
    takeEvery(actions.SHARE_CHAT, shareChatSaga),
    takeEvery(actions.RENAME, renameSaga),
    takeEvery(actions.DELETE, delSaga),
    takeEvery(actions.COPY_MESSAGE, copyMessageSaga),
    takeEvery(actions.UPDATE_DRAFT_MESSAGE, updateDraftMessageSaga),
    takeEvery(actions.APPROVE_TOOL_CALLS, approveToolCallsSaga),
    takeEvery(actions.APPROVE_CARD_TOOL_CALLS, approveCardToolCallsSaga),
    takeEvery(actions.REJECT_TOOL_CALLS, rejectToolCallsSaga),
    fork(watchToolCallInputUpdates),
    takeEvery(actions.REJECT_CARD_TOOL_CALLS, rejectCardToolCallsSaga),
    takeEvery(actions.UNDO_REJECT_TOOL_CALLS, undoRejectToolCallsSaga),
    takeEvery(router.UPDATE_ROUTER, watchRouterSaga),
    debounce(200, _isAiChatSnapshot, boardCreatedFromChatSaga),
    debounce(200, _isToolMessagesSnapshot, loadCardsFromToolCallsSaga),
  ]);
}

export default saga;
